/**
 * G5 (AC-GP-029) — no dead buttons.
 *
 * `TaskErrorResolver` used to render a generic «Retry» for EVERY failed task and
 * announce it on the `task-error:resolve` browser event. Exactly one surface ever
 * listened (`ContentPlansPage`), and only for two content-plan codes — so on a
 * failed strategy generation the owner pressed a button that did nothing.
 *
 * A task type is now offered a generic action only while a surface has REGISTERED
 * a handler for it. The registry is a plain module-level map: the tasks panel is
 * global (the canvas hosts it) and its resolver must not prop-drill through every
 * host, while a surface that unmounts takes its handler with it.
 *
 * Fix round 1:
 * - a handler ANSWERS (M6/I1): `true` = it acted, `false` = it could not. A `false`
 *   is shown to the owner; a button is never silently inert.
 * - registrations are a STACK per type (M6): the newest surface wins, unregistering
 *   removes only its own entry, and an earlier still-mounted registrant keeps the
 *   type handled.
 * - `subscribeTaskResolveHandlers` lets an already-open dialog light its button when
 *   a surface registers late.
 *
 * The strategy actions («Save again», «Generate again») are NOT registered here —
 * they are built into the module and call the builder API directly.
 */

export interface TaskResolveDetail {
  /** `task.errorCode` (or `'UNKNOWN'`). */
  code: string;
  /** What the resolver asked for — today only `'retry'` reaches a registered handler. */
  action: string;
  task: Record<string, unknown>;
  meta?: Record<string, unknown>;
}

/** `true` = handled. `false` (or nothing) = this surface could not act on it. */
export type TaskResolveHandler = (detail: TaskResolveDetail) => boolean | void;

const handlers = new Map<string, TaskResolveHandler[]>();
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of [...listeners]) listener();
}

/**
 * Register `handler` for `taskType` and get its unregister function back. Call it
 * from an effect's cleanup — a surface that is not mounted cannot resolve anything.
 * Registrations stack: the newest wins, and unregistering removes only this entry.
 */
export function registerTaskResolveHandler(taskType: string, handler: TaskResolveHandler): () => void {
  const stack = handlers.get(taskType) ?? [];
  stack.push(handler);
  handlers.set(taskType, stack);
  notify();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    const current = handlers.get(taskType);
    if (!current) return;
    const index = current.lastIndexOf(handler);
    if (index === -1) return;
    current.splice(index, 1);
    if (current.length === 0) handlers.delete(taskType);
    notify();
  };
}

/** True while some mounted surface can actually act on this task type. */
export function hasTaskResolveHandler(taskType: string | undefined): boolean {
  return Boolean(taskType && (handlers.get(taskType)?.length ?? 0) > 0);
}

/** React to registrations and unregistrations (`useSyncExternalStore`). */
export function subscribeTaskResolveHandlers(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/**
 * Runs the top registered handler for the task's type.
 * `false` means nothing acted — no handler at all, or one that answered `false`;
 * the caller must say so instead of leaving the press silent.
 */
export function resolveTaskError(detail: TaskResolveDetail): boolean {
  const stack = handlers.get(String(detail.task?.taskType ?? ''));
  const handler = stack?.[stack.length - 1];
  if (!handler) return false;
  return handler(detail) === true;
}
